import type { Dispatch, SetStateAction } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { BulkSidebarShell, DangerZoneCard } from '../../components/bulkSidebar';
import DocumentTagsCard from '../../components/DocumentTagsCard';
import { Button } from '../../catalyst/button';
import { Input } from '../../catalyst/input';
import { Label } from '../../catalyst/fieldset';
import { Radio, RadioField, RadioGroup } from '../../catalyst/radio';
import backend from '../../services/backend';
import { usePlatformData } from '../../hooks/usePlatformData';

type CategoriesState = ReturnType<typeof usePlatformData>['categories'];

const RESET_DEPTHS = [
  { value: 1, label: 'Depth 1: Reset classification only' },
  { value: 2, label: 'Depth 2: Reset embeddings and classification' },
  { value: 3, label: 'Depth 3: Reset text, embeddings, and classification' },
  { value: 4, label: 'Depth 4: Reset pages, pdfParser, embeddings and classification' },
] as const;

type ResetDepth = (typeof RESET_DEPTHS)[number]['value'];

export interface DocumentsBulkSidebarProps {
  selectedIds: Set<string>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
  categories: CategoriesState;
  selectedCategory: string;
  setSelectedCategory: Dispatch<SetStateAction<string>>;
  isUpdatingCategory: boolean;
  categoryError: string | null;
  onUpdateCategory: () => void;
  onRemoveCategory: () => void;
  selectedDocumentsTags: string[];
  onTagsUpdated: () => void | Promise<void>;
  isDangerZoneExpanded: boolean;
  setIsDangerZoneExpanded: Dispatch<SetStateAction<boolean>>;
  resetDepth: ResetDepth;
  setResetDepth: Dispatch<SetStateAction<ResetDepth>>;
  onResetSelected: () => void;
  isResetting: boolean;
  resetError: string | null;
  onDeleteSelected: () => void;
  isDeleting: boolean;
  deleteError: string | null;
  isRetryingFailedSteps: boolean;
  setIsRetryingFailedSteps: Dispatch<SetStateAction<boolean>>;
}

export function DocumentsBulkSidebar({
  selectedIds,
  setSelectedIds,
  categories,
  selectedCategory,
  setSelectedCategory,
  isUpdatingCategory,
  categoryError,
  onUpdateCategory,
  onRemoveCategory,
  selectedDocumentsTags,
  onTagsUpdated,
  isDangerZoneExpanded,
  setIsDangerZoneExpanded,
  resetDepth,
  setResetDepth,
  onResetSelected,
  isResetting,
  resetError,
  onDeleteSelected,
  isDeleting,
  deleteError,
  isRetryingFailedSteps,
  setIsRetryingFailedSteps,
}: DocumentsBulkSidebarProps) {
  const categoryOptions = categories.data?.documentCategories ?? [];

  return (
    <BulkSidebarShell selectedCount={selectedIds.size} onDeselectAll={() => setSelectedIds(new Set())}>
      <div className="bg-zinc-100 sm:rounded-lg overflow-hidden mb-4 dark:bg-zinc-800">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100 mb-4">Set Category</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="bulk-category" className="block text-sm font-medium text-gray-700 dark:text-zinc-100 mb-1">
                Category
              </label>
              <Input
                id="bulk-category"
                list="bulk-category-list"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                placeholder="Select or enter a category"
                disabled={categories.isLoading}
                autoComplete="off"
              />
              <datalist id="bulk-category-list">
                {categoryOptions.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </div>
            <div className="flex gap-2">
              <Button
                color="blue"
                className="flex-1"
                onClick={onUpdateCategory}
                disabled={isUpdatingCategory || !selectedCategory}
              >
                {isUpdatingCategory ? 'Updating...' : 'Set Category'}
              </Button>
              <Button
                color="zinc"
                onClick={onRemoveCategory}
                disabled={isUpdatingCategory}
              >
                {isUpdatingCategory ? 'Updating...' : 'Remove'}
              </Button>
            </div>
            {categoryError && <p className="text-sm text-red-600 dark:text-red-400">{categoryError}</p>}
          </div>
        </div>
      </div>

      <DocumentTagsCard
        selectedIds={selectedIds}
        selectedDocumentsTags={selectedDocumentsTags}
        onTagsUpdated={onTagsUpdated}
      />

      <DangerZoneCard
        expanded={isDangerZoneExpanded}
        onToggle={() => setIsDangerZoneExpanded(!isDangerZoneExpanded)}
      >
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-zinc-100">Reset Depth</label>
          <RadioGroup value={String(resetDepth)} onChange={(value) => setResetDepth(Number(value) as ResetDepth)}>
            {RESET_DEPTHS.map((depth) => (
              <RadioField key={depth.value}>
                <Radio value={String(depth.value)} />
                <Label>{depth.label}</Label>
              </RadioField>
            ))}
          </RadioGroup>
        </div>
        <Button
          color="amber"
          className="w-full"
          onClick={onResetSelected}
          disabled={isResetting}
        >
          {isResetting ? 'Resetting...' : 'Reset Selected Documents'}
        </Button>
        {resetError && <p className="text-sm text-red-600 dark:text-red-400">{resetError}</p>}
        <Button
          color="red"
          className="w-full"
          onClick={onDeleteSelected}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Permanently Delete'}
        </Button>
        {deleteError && <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p>}
        <hr />
        <Button
          color="amber"
          className="w-full"
          onClick={() => {
            setIsRetryingFailedSteps(true);
            Promise.all(Array.from(selectedIds).map((id) => backend.recoverDocument(id)))
              .then(() => {
                setSelectedIds(new Set());
                setIsDangerZoneExpanded(false);
              })
              .finally(() => {
                setIsRetryingFailedSteps(false);
              });
          }}
          disabled={isRetryingFailedSteps}
        >
          <WrenchScrewdriverIcon data-slot="icon" className="h-4 w-4" />
          {isRetryingFailedSteps ? 'Recovering...' : 'Recover'}
        </Button>
      </DangerZoneCard>
    </BulkSidebarShell>
  );
}
